import { Link } from 'react-router-dom';
import { Flame, LogOut, Monitor, Moon, Shield, Sun, Trophy, Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme, type ThemePref } from '../contexts/ThemeContext';
import { useMyStats } from '../lib/data';
import Mark from './Mark';

const THEME_ICON = { light: Sun, dark: Moon, system: Monitor } as const;
const THEME_NEXT: Record<ThemePref, string> = { light: 'Dark', dark: 'System', system: 'Light' };

/**
 * The app header. Neutral surface; terracotta is reserved for the mark + the
 * reward signals (Zor pill, Kasrat streak) per the brand rule. The Zor/streak
 * read live from the user doc, so they animate the instant the scoring function
 * commits.
 */
export default function SiteHeader() {
  const { user, profile, isAdmin, isCandidate, logout } = useAuth();
  const { points, streak } = useMyStats(user?.uid);
  const { pref, cycle } = useTheme();
  const ThemeIcon = THEME_ICON[pref];
  const themeLabel = `Theme: ${pref}. Switch to ${THEME_NEXT[pref]}`;

  // The Zor pill links to the Dangal for staff; for candidates (who have no
  // Dangal) it's a non-navigating status pill of their OWN Zor.
  const zorPill = points && (
    <span className="flex items-center gap-1 rounded-full bg-terracotta-50 px-2.5 py-1 text-sm font-semibold text-terracotta-700 ring-1 ring-inset ring-terracotta-200 dark:bg-terracotta-900/30 dark:text-terracotta-200 dark:ring-terracotta-700">
      <Trophy className="h-3.5 w-3.5" />
      <span>{points.month}</span>
      <span className="hidden text-[11px] font-normal opacity-70 sm:inline">Zor</span>
    </span>
  );

  return (
    <header className="sticky top-0 z-20 border-b border-rule bg-paper-0/90 backdrop-blur dark:border-clay-rule dark:bg-clay-surface/90">
      <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-4 py-2.5">
        <Link to="/" className="flex items-center gap-2">
          <Mark className="h-7 w-7 text-terracotta-500" />
          <span className="leading-none">
            <span className="wordmark text-lg text-ink-900 dark:text-paper-50">Akhara</span>
            <span className="ml-1 align-middle text-[10px] uppercase tracking-wider text-ink-300">
              by Henoyo
            </span>
          </span>
        </Link>

        <div className="flex items-center gap-2">
          {isCandidate && (
            <span className="rounded-full bg-verdant-200/60 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-verdant-700 dark:bg-verdant-700/25 dark:text-verdant-200">
              Aspirant
            </span>
          )}
          {points &&
            (isCandidate ? (
              <span title="Your Zor this month">{zorPill}</span>
            ) : (
              <Link to="/dangal" title="Your Zor this month">
                {zorPill}
              </Link>
            ))}
          {streak && streak.count > 0 && (
            <span
              title={`Kasrat — ${streak.count}-day streak`}
              className="flex items-center gap-1 rounded-full bg-terracotta-500 px-2.5 py-1 text-sm font-semibold text-white shadow-sm"
            >
              <Flame className="h-3.5 w-3.5 motion-safe:animate-pulse" />
              {streak.count}
            </span>
          )}
          {profile?.isMentor && (
            <Link
              to="/shagird"
              title="My Shagird — mentee progress"
              className="rounded-full p-1.5 text-ink-500 hover:bg-paper-100 dark:text-paper-300 dark:hover:bg-clay-raised"
            >
              <Users className="h-4 w-4" />
            </Link>
          )}
          {isAdmin && (
            <Link
              to="/admin"
              title="Khalifa — who's active"
              className="rounded-full p-1.5 text-ink-500 hover:bg-paper-100 dark:text-paper-300 dark:hover:bg-clay-raised"
            >
              <Shield className="h-4 w-4" />
            </Link>
          )}
          <button
            onClick={cycle}
            type="button"
            title={themeLabel}
            aria-label={themeLabel}
            className="rounded-full p-1.5 text-ink-400 hover:bg-paper-100 hover:text-ink-700 dark:text-paper-300 dark:hover:bg-clay-raised"
          >
            <ThemeIcon className="h-4 w-4" />
          </button>
          <span
            className="flex h-7 w-7 items-center justify-center rounded-full bg-verdant-700 text-[11px] font-semibold text-white"
            title={profile?.displayName || profile?.email}
          >
            {profile?.initials || '··'}
          </span>
          <button
            onClick={() => void logout()}
            title="Sign out"
            className="rounded-full p-1.5 text-ink-400 hover:bg-paper-100 hover:text-ink-700 dark:hover:bg-clay-raised"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </header>
  );
}
